import type { User } from 'types/user.types';
import { ROLES } from 'constants/roles';

// ── V2 Role constants ──────────────────────────────────────────────────────────
export const SYSTEM_ADMIN = ROLES.SYSTEM_ADMIN_V2.name;
export const ACCOUNT_ADMIN = ROLES.ACCOUNT_ADMIN_V2.name;
export const SOLUTION_ADMIN = ROLES.SOLUTION_ADMIN.name;
export const SE_ADMIN = ROLES.SE_ADMIN.name;
export const HXC_ADMIN = ROLES.HXC_ADMIN.name;
export const DATA_ADMIN = ROLES.DATA_ADMIN.name;


// ── Section definitions ────────────────────────────────────────────────────────
// Maps a top-level app section to the V2 roles that grant access.
// SYSTEM_ADMIN is always included — it can access everything.

export type AppSection =
  | 'accounts'
  | 'dashboards'
  | 'alerts'
  | 'hxc-partners'
  | 'comp-sets'
  | 'data-access-control'
  | 'program-access'
  | 'change-log'
  | 'master-data'
  | 'region-sync'
  | 'participants';

const SECTION_ROLES: Record<AppSection, string[] | null> = {
  'accounts':              [SYSTEM_ADMIN, ACCOUNT_ADMIN],
  'dashboards':            [SE_ADMIN],
  'alerts':                [SE_ADMIN],
  'hxc-partners':          [HXC_ADMIN],
  'comp-sets':             [DATA_ADMIN],
  'data-access-control':   [DATA_ADMIN],
  'program-access':        [HXC_ADMIN],
  'change-log':            null, // Accessible by everyone
  'master-data':           [DATA_ADMIN],
  'region-sync':           [SYSTEM_ADMIN],
  'participants':          [SOLUTION_ADMIN],
};

// ── Helpers ────────────────────────────────────────────────────────────────────

/**
 * Return the user's effective V2 roles.
 *
 * - If the `roles` array is populated, use it as-is.
 * - Otherwise, if the legacy `role` is a System Admin or System Super Admin,
 *   treat the user as having SYSTEM_ADMIN (full access).
 * - Otherwise return empty — which means "no roles, non-admin legacy user".
 *   Those users are handled by `isLegacyAdmin` checks.
 */
export function getEffectiveRoles(user: User | null | undefined): string[] {
  const roles = user?.accountProfile?.roles;
  if (roles && roles.length > 0) return roles.map((r) => r.name);

  return [];
}

/**
 * True when the user is a system-level admin: SYSTEM_ADMIN_V2 (effective) or legacy admin.
 * Used to gate elevated capabilities (e.g. editing/deleting versioned master data) that
 * are too risky for narrower admin roles like DATA_ADMIN.
 */
export function isSystemAdmin(user: User | null | undefined): boolean {
  return getEffectiveRoles(user).includes(SYSTEM_ADMIN);
}

/**
 * Check whether a user can access a given app section.
 */
export function canAccess(user: User | null | undefined, section: AppSection): boolean {
  const requiredRoles = SECTION_ROLES[section];

  // null = no guard (everyone allowed)
  if (requiredRoles === null) return true;

  const effective = getEffectiveRoles(user);
  return effective.some((r) => requiredRoles.includes(r));
}

/**
 * Determine the default landing route for the current user based on role priority.
 */
export function getDefaultRoute(user: User | null | undefined): string {
  const effective = getEffectiveRoles(user);

  // No new roles AND not a legacy admin → Change Log (everyone can see it)
  if (effective.length === 0) return '/change-log';

  // Priority order for new-role users
  if (effective.includes(SYSTEM_ADMIN)) return '/accounts';
  if (effective.includes(ACCOUNT_ADMIN)) return '/accounts';
  if (effective.includes(SE_ADMIN)) return '/dashboards';
  if (effective.includes(HXC_ADMIN)) return '/hxc-partners/staging';
  if (effective.includes(DATA_ADMIN)) return '/data-access-control';

  // Fallback: Change Log is accessible by everyone
  return '/change-log';
}
